/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV7KachingNativeDowngrade) return;

  const KACHING = '__kaching_bundles';
  const CART_TIMEOUT_MS = 6000;
  let operation = null;
  const publish = (state) => {
    document.documentElement.dataset.safewalkGiftReconcile = state;
    document.documentElement.dataset.safewalkBundleDowngrade = state;
    document.dispatchEvent(new CustomEvent('safewalk:real-gifts-state', {
      detail: { state }
    }));
  };
  const sectionIds = () => {
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    document.querySelector('cart-drawer')?.getSectionsToRender?.()
      .forEach((section) => ids.add(section.id));
    return [...ids].filter(Boolean).slice(0, 5);
  };
  const fetchJson = async (input, init = {}) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeout;
    const request = (async () => {
      const requestInit = { ...init };
      if (controller) requestInit.signal = controller.signal;
      const response = await window.fetch(input, requestInit);
      return { response, body: await response.json() };
    })();
    const deadline = new Promise((_, reject) => {
      timeout = window.setTimeout(() => {
        controller?.abort();
        reject(new Error('cart-timeout'));
      }, CART_TIMEOUT_MS);
    });
    try {
      return await Promise.race([request, deadline]);
    } finally {
      window.clearTimeout(timeout);
    }
  };
  const fetchCart = async () => {
    const result = await fetchJson('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!result.response.ok || !result.body?.items) throw new Error('cart-read');
    return result.body;
  };
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const identityRole = (item, config) => (config.identities || []).find((identity) => (
    Number(item?.product_id || 0) === Number(identity.productId)
      && variantId(item) === Number(identity.variantId)
  ))?.role || '';
  const linkedToMain = (item, main) => {
    const parentKey = String(item?.parent_relationship?.parent_key || '');
    if (parentKey) return parentKey === String(main?.key || '');
    return Number(item?.parent_id || 0) === variantId(main);
  };
  const exactP2Group = (cart, item, contract, config) => {
    const classification = contract?.classifyCart?.(cart, config) || {};
    const group = (classification.groups || []).find((candidate) => (
      candidate.kind === 'harness_bungee'
        && candidate.complete
        && candidate.items?.includes(item)
    ));
    if (!group || group.items.length !== 5 || group.gifts?.length !== 3) return null;
    const companions = group.items.filter((entry) => (
      entry !== group.main && !identityRole(entry, config)
    ));
    if (companions.length !== 1 || companions[0] !== item) return null;
    const metadata = contract.metadataFor(item);
    const mainMetadata = contract.metadataFor(group.main);
    const exactCompanion = Number(item.quantity) === 1
      && lineTotal(item) > 0
      && linkedToMain(item, group.main)
      && metadata?.deal === config.deal
      && metadata?.id === mainMetadata?.id
      && metadata?.v === mainMetadata?.v
      && metadata?.bar === config.bars?.harness_bungee
      && Boolean(metadata?.bundleProduct);
    return exactCompanion ? group : null;
  };
  const childProperties = (item, role) => {
    if (role !== 'gift_tags') return {};
    const copy = window.SafeWalkV6RealGiftCopy || {};
    const dog = copy.dogNameProperty || 'שם הכלב';
    const phone = copy.phoneProperty || 'מספר טלפון';
    const source = propertiesFor(item);
    return {
      ...(String(source[dog] || '').trim() ? { [dog]: String(source[dog]).trim() } : {}),
      ...(String(source[phone] || '').trim() ? { [phone]: String(source[phone]).trim() } : {})
    };
  };
  const p1Additions = (group, config, contract) => {
    const mainMetadata = contract.metadataFor(group.main);
    const mainId = variantId(group.main);
    if (!mainMetadata?.id || !mainMetadata?.v || !mainId
      || !config.bars?.harness_only) return [];
    const downgradedMain = { ...mainMetadata, bid: config.bars.harness_only };
    delete downgradedMain.bar;
    const roleLines = new Map();
    for (const item of group.gifts || []) roleLines.set(identityRole(item, config), item);
    const additions = [{
      id: mainId,
      quantity: 1,
      properties: { ...propertiesFor(group.main), [KACHING]: JSON.stringify(downgradedMain) }
    }];
    for (const identity of config.identities || []) {
      additions.push({
        id: Number(identity.variantId),
        quantity: 1,
        parent_id: mainId,
        properties: {
          ...childProperties(roleLines.get(identity.role), identity.role),
          [KACHING]: JSON.stringify({
            id: String(mainMetadata.id),
            deal: config.deal,
            v: mainMetadata.v,
            gift: config.realGiftIds?.harness_only?.[identity.role]
          })
        }
      });
    }
    return additions;
  };
  const originalAdditions = (group) => {
    const mainId = variantId(group.main);
    return [group.main, ...group.items.filter((item) => item !== group.main)].map((item) => ({
      id: variantId(item),
      quantity: 1,
      ...(item === group.main ? {} : { parent_id: mainId }),
      properties: { ...propertiesFor(item) }
    }));
  };
  const removeKeys = async (keys) => {
    const result = await fetchJson('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(keys.map((key) => [key, 0])),
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!result.response.ok || !result.body?.items
      || result.body.items.some((item) => keys.includes(item.key))) {
      throw new Error('group-remove');
    }
    return result.body;
  };
  const addItems = async (items) => {
    const result = await fetchJson('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!result.response.ok || !result.body?.items) throw new Error('group-add');
    return result.body;
  };
  const removeSameGroup = async (groupId, contract, config) => {
    const cart = await fetchCart();
    const keys = cart.items.filter((item) => {
      const metadata = contract.metadataFor(item);
      return metadata?.deal === config.deal && String(metadata?.id || '') === String(groupId);
    }).map((item) => item.key).filter(Boolean);
    if (keys.length) await removeKeys(keys);
  };
  const restoreP2 = async (original, groupId, contract, config) => {
    await removeSameGroup(groupId, contract, config);
    await addItems(original);
    const restored = await fetchCart();
    const group = (contract.classifyCart(restored, config).groups || []).find((candidate) => (
      candidate.groupId === groupId && candidate.kind === 'harness_bungee'
    ));
    if (!group || !exactP2Group(restored, group.items.find((item) => (
      item !== group.main && !identityRole(item, config)
    )), contract, config)) throw new Error('p2-restore-verify');
    return restored;
  };
  const downgradeLine = async (lineIndex, expectedKey = '') => {
    if (operation) return operation;
    operation = (async () => {
      publish('pending');
      const contract = window.SafeWalkV7KachingNativeContract;
      const config = contract?.configFromRegistry?.() || {};
      const cart = await fetchCart();
      const item = cart.items[Number(lineIndex) - 1];
      if (!item || (expectedKey && item.key !== expectedKey)) throw new Error('stale-line');
      const group = exactP2Group(cart, item, contract, config);
      if (!group) throw new Error('not-exact-p2-companion');
      const additions = p1Additions(group, config, contract);
      const original = originalAdditions(group);
      const removalKeys = group.items.map((entry) => entry.key).filter(Boolean);
      if (additions.length !== 4 || original.length !== 5
        || removalKeys.length !== group.items.length) throw new Error('p2-downgrade-contract');
      let removed = false;
      try {
        await removeKeys(removalKeys);
        removed = true;
        const added = await addItems(additions);
        window.SafeWalkV6RealGifts?.render?.(added);
        const verified = await fetchCart();
        const repaired = (contract.classifyCart(verified, config).groups || []).find((candidate) => (
          candidate.groupId === group.groupId
            && candidate.kind === 'harness_only'
            && candidate.complete
            && candidate.items.length === 4
            && variantId(candidate.main) === variantId(group.main)
            && candidate.items.every((entry) => !contract.metadataFor(entry)?.bundleProduct)
        ));
        if (!repaired) throw new Error('p2-downgrade-verify');
        window.SafeWalkV7KachingNativeCart = verified;
        window.SafeWalkV6RealGiftLifecycle?.inspectCart?.(verified);
        publish('ready');
        return verified;
      } catch (error) {
        if (removed) {
          const restored = await restoreP2(original, group.groupId, contract, config);
          window.SafeWalkV7KachingNativeCart = restored;
          window.SafeWalkV6RealGiftLifecycle?.inspectCart?.(restored);
        }
        throw error;
      }
    })().catch((error) => {
      publish('invalid');
      window.setTimeout(() => window.location.reload(), 0);
      throw error;
    }).finally(() => {
      operation = null;
    });
    return operation;
  };
  const onClick = (event) => {
    const button = event.target?.closest?.('cart-drawer cart-remove-button button');
    const remove = button?.closest?.('cart-remove-button[data-index]');
    if (!remove) return;
    const lineIndex = Number(remove.dataset.index || 0);
    const contract = window.SafeWalkV7KachingNativeContract;
    const config = contract?.configFromRegistry?.() || {};
    const cart = window.SafeWalkV7KachingNativeCart;
    const item = cart?.items?.[lineIndex - 1];
    if (!exactP2Group(cart, item, contract, config)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    return downgradeLine(lineIndex, item.key).catch(() => {});
  };

  window.SafeWalkV7KachingNativeDowngrade = Object.freeze({
    downgradeLine,
    onClick
  });
  document.addEventListener('click', onClick, true);
})();
